import { useMemo } from 'react';

import { useAppSelector } from 'src/app/store/hooks';
import { DispatchRead } from 'src/common/external/bambi-api/bambiApi';

import { createClockInEventsFromAssignments } from './mobiscrollTransformers/createClockInEventsFromAssignments';
import { createClockOutEventsFromAssignments } from './mobiscrollTransformers/createClockOutEventsFromAssignments';
import { createEventsFromBreaks } from './mobiscrollTransformers/createEventsFromBreaks';
import { createEventsFromTrips } from './mobiscrollTransformers/createEventsFromTrips';
import { createEventsFromTripWaitTimes } from './mobiscrollTransformers/createEventsFromTripWaitTimes';
import { createInvalidsFromAssignments } from './mobiscrollTransformers/createInvalidsFromAssignments';
import { createResourcesFromAssignments } from './mobiscrollTransformers/createResourcesFromAssignments';

export function useDispatchEventResources(dispatchResponse?: DispatchRead) {
  const selectedDrivers = useAppSelector(
    (state) => state.dispatcher.dispatchCalendarFilters.drivers
  );
  const resources = useMemo(() => {
    let resources = dispatchResponse?.assignments
      ? createResourcesFromAssignments(dispatchResponse.assignments)
      : [];
    if (selectedDrivers.length) {
      const driverIds = selectedDrivers.map(
        // Apparently the id here is the membership id, not the driver id
        (driver) => driver.driver
      );
      resources = resources.filter((resource) =>
        driverIds.includes(resource.assignment.driver.id)
      );
    }

    return resources;
  }, [dispatchResponse?.assignments, selectedDrivers]);

  const assignedTripEvents = useMemo(() => {
    return dispatchResponse?.assigned_trips
      ? createEventsFromTrips(dispatchResponse.assigned_trips)
      : [];
  }, [dispatchResponse?.assigned_trips]);

  const breakEvents = useMemo(() => {
    return dispatchResponse?.assignments
      ? createEventsFromBreaks(dispatchResponse.assignments)
      : [];
  }, [dispatchResponse?.assignments]);

  const waitTimeEvents = useMemo(() => {
    return dispatchResponse?.assigned_trips
      ? createEventsFromTripWaitTimes(dispatchResponse.assigned_trips)
      : [];
  }, [dispatchResponse?.assigned_trips]);

  const invalids = useMemo(() => {
    return dispatchResponse?.assignments
      ? createInvalidsFromAssignments(dispatchResponse.assignments)
      : [];
  }, [dispatchResponse?.assignments]);

  const clockInEvents = useMemo(() => {
    return dispatchResponse?.assignments
      ? createClockInEventsFromAssignments(dispatchResponse.assignments)
      : [];
  }, [dispatchResponse?.assignments]);

  const clockOutEvents = useMemo(() => {
    return dispatchResponse?.assignments
      ? createClockOutEventsFromAssignments(dispatchResponse.assignments)
      : [];
  }, [dispatchResponse?.assignments]);

  const events = useMemo(
    () => [
      ...assignedTripEvents,
      ...breakEvents,
      ...waitTimeEvents,
      ...clockInEvents,
      ...clockOutEvents,
    ],
    [
      assignedTripEvents,
      breakEvents,
      clockInEvents,
      clockOutEvents,
      waitTimeEvents,
    ]
  );

  return {
    resources,
    invalids,
    events,
  };
}
